import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { ChevronUp, ChevronDown, Search, Info, Layers } from 'lucide-react';
import TherapeuticAlternativesTable from './TherapeuticAlternativesTable';
import './AlternativesTab.css';

/**
 * AlternativesTab - Displays covered and therapeutic alternatives
 * Data is fetched and cached at parent level (UnifiedLanding)
 */
const AlternativesTab = ({ 
  alternatives = [],
  therapeuticAlternatives = null,
  alternativesLoading = false,
  therapeuticAlternativesLoading = false,
  alternativesError = null,
  therapeuticAlternativesError = null,
  originalTherapeuticClassId
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState('covered');
  
  const itemsPerPage = 15;

  const hasNoCoveredAlternatives = !alternatives || alternatives.length === 0;
  const hasTherapeuticAlternatives = therapeuticAlternatives && therapeuticAlternatives.length > 0;

  useEffect(() => {
    // If no covered alternatives but has therapeutic alternatives, switch to therapeutic tab
    if (hasNoCoveredAlternatives && hasTherapeuticAlternatives) {
      setActiveTab('therapeutic');
    }
  }, [hasNoCoveredAlternatives, hasTherapeuticAlternatives]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedAlternatives = useMemo(() => {
    if (!sortColumn || !sortDirection) return alternatives;
    
    return [...alternatives].sort((a, b) => {
      let compareA;
      let compareB;
      
      switch (sortColumn) {
        case 'rank':
          compareA = a.rank;
          compareB = b.rank;
          break;
        case 'drugName':
          compareA = a.drugName.toLowerCase();
          compareB = b.drugName.toLowerCase();
          break;
        case 'type':
          compareA = a.isGeneric ? 'generic' : 'brand';
          compareB = b.isGeneric ? 'generic' : 'brand';
          break;
        case 'tier':
          compareA = a.drugListTier.toLowerCase();
          compareB = b.drugListTier.toLowerCase();
          break;
        case 'umFlags':
          compareA = [a.umFlags.priorAuthorization, a.umFlags.stepTherapy, a.umFlags.quantityLimit].filter(Boolean).length;
          compareB = [b.umFlags.priorAuthorization, b.umFlags.stepTherapy, b.umFlags.quantityLimit].filter(Boolean).length;
          break;
        default:
          return 0;
      }
      
      if (compareA < compareB) return sortDirection === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [alternatives, sortColumn, sortDirection]);

  const searchFilteredAlternatives = useMemo(() => {
    if (!searchQuery.trim()) return sortedAlternatives;
    
    const query = searchQuery.toLowerCase();
    return sortedAlternatives.filter(alt => 
      alt.drugName.toLowerCase().includes(query) ||
      (alt.drugDescription && alt.drugDescription.toLowerCase().includes(query)) ||
      alt.drugListTier.toLowerCase().includes(query) ||
      alt.formularyTier.toLowerCase().includes(query)
    );
  }, [sortedAlternatives, searchQuery]);

  const totalPages = Math.ceil(searchFilteredAlternatives.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAlternatives = searchFilteredAlternatives.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortColumn, sortDirection, searchQuery]);

  const getRankBadgeClass = (rank) => {
    if (rank === 1) return "bg-status-covered/10 text-status-covered border-status-covered/30";
    return "bg-gray-100 text-gray-800 border-gray-300";
  };

  const SortableTableHead = ({ column, children }) => {
    const isActive = sortColumn === column;
    return (
      <th className="cursor-pointer hover:bg-muted/50 transition-colors p-3" onClick={() => handleSort(column)}>
        <div className="flex items-center gap-2">
          <span className="font-medium">{children}</span>
          {isActive ? (
            sortDirection === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          ) : (
            <div className="h-4 w-4 opacity-30">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
              </svg>
            </div>
          )}
        </div>
      </th>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        pages.push(
          <button
            key={i}
            onClick={() => setCurrentPage(i)}
            className={`px-3 py-1 rounded ${
              currentPage === i
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted'
            }`}
          >
            {i}
          </button>
        );
      } else if (i === currentPage - 2 || i === currentPage + 2) {
        pages.push(<span key={i} className="px-2">...</span>);
      }
    }

    return (
      <div className="flex justify-between items-center mt-6 px-4 py-4">
        <div className="text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(endIndex, searchFilteredAlternatives.length)} of {searchFilteredAlternatives.length} alternatives
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {pages}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">Alternative Medications</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Explore covered alternatives on this plan and therapeutic alternatives by class
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="flex items-start justify-between gap-3 sm:gap-4 flex-col md:flex-row">
            <TabsList className="bg-muted flex-wrap h-auto gap-2 p-2 rounded-lg border shadow-sm">
              <TabsTrigger value="covered" className="text-xs sm:text-sm md:text-base font-bold min-h-[44px] px-3 sm:px-4 md:px-6 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
                Covered Alternatives
              </TabsTrigger>
              <TabsTrigger value="therapeutic" className="text-xs sm:text-sm md:text-base font-bold min-h-[44px] px-3 sm:px-4 md:px-6 transition-all duration-200 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary">
                Therapeutic Alternatives
              </TabsTrigger>
            </TabsList>
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={activeTab === "covered" ? "Search alternatives..." : "Search therapeutic..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 sm:pr-4 py-2 text-sm sm:text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          
          <TabsContent value="covered" className="mt-6">
            {alternativesLoading ? (
              <div className="p-8 sm:p-12">
                <div className="flex flex-col items-center justify-center space-y-3 sm:space-y-4">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary"></div>
                  <p className="text-xs sm:text-sm font-medium">Loading alternatives...</p>
                </div>
              </div>
            ) : alternativesError || alternatives.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 text-center space-y-3 sm:space-y-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-muted flex items-center justify-center">
                  <Info className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-foreground">
                    No Covered Alternatives Listed
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    This plan does not list covered alternative medications for this drug. Check Therapeutic Alternatives for similar medications by drug class.
                  </p>
                </div>
              </div>
            ) : (
              <div>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto max-h-[500px] lg:max-h-[600px] overflow-y-auto border rounded">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-background border-b">
                      <tr>
                        <SortableTableHead column="rank">Rank</SortableTableHead>
                        <SortableTableHead column="drugName">Drug Name</SortableTableHead>
                        <SortableTableHead column="type">Type</SortableTableHead>
                        <SortableTableHead column="tier">Tier</SortableTableHead>
                        <th className="p-3 text-left font-medium">Coverage</th>
                        <SortableTableHead column="umFlags">Flags</SortableTableHead>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAlternatives.map((alt) => (
                        <tr key={alt.productId} className="border-b">
                          <td className="p-3">
                            <Badge variant="static" className={getRankBadgeClass(alt.rank)}>
                              #{alt.rank}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="font-medium">{alt.drugName}</div>
                            {alt.drugDescription && alt.drugDescription.toLowerCase() !== alt.drugName.toLowerCase() && (
                              <div className="text-sm text-muted-foreground mt-0.5">{alt.drugDescription}</div>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="static" className="text-xs bg-white text-black border-gray-300">
                            {alt.isGeneric ? "Generic" : "Brand"}
                          </Badge>
                        </td>
                        <td className="p-3">
                            <div className="text-sm">
                              <div>{alt.drugListTier}</div>
                              {alt.formularyTier !== alt.drugListTier && (
                                <div className="text-muted-foreground">{alt.formularyTier}</div>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge className="bg-status-covered text-white border-0">
                              Covered
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1 flex-wrap">
                              {alt.umFlags.priorAuthorization && (
                                <Badge variant="static" className="text-xs">PA</Badge>
                              )}
                              {alt.umFlags.stepTherapy && (
                                <Badge variant="static" className="text-xs">ST</Badge>
                              )}
                              {alt.umFlags.quantityLimit && (
                                <Badge variant="static" className="text-xs">QL</Badge>
                              )}
                              {!alt.umFlags.priorAuthorization && !alt.umFlags.stepTherapy && !alt.umFlags.quantityLimit && (
                                <span className="text-muted-foreground text-sm">None</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {renderPagination()}
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-2 sm:space-y-3">
                  {paginatedAlternatives.map((alt) => (
                    <Card key={alt.productId} className="p-3 sm:p-4">
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm sm:text-base">{alt.drugName}</div>
                            {alt.drugDescription && alt.drugDescription.toLowerCase() !== alt.drugName.toLowerCase() && (
                              <div className="text-xs sm:text-sm text-muted-foreground mt-0.5 line-clamp-1">{alt.drugDescription}</div>
                            )}
                          </div>
                          <Badge variant="static" className={`${getRankBadgeClass(alt.rank)} shrink-0`}>
                            #{alt.rank}
                          </Badge>
                        </div>

                        <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                          <Badge variant="static" className="text-[10px] sm:text-xs bg-white text-black border-gray-300">
                            {alt.isGeneric ? "Generic" : "Brand"}
                          </Badge>
                          <Badge className="bg-status-covered text-white border-0 text-[10px] sm:text-xs">
                            Covered
                          </Badge>
                        </div>

                        <div className="text-sm">
                          <div className="font-medium">Tier</div>
                          <div>{alt.drugListTier}</div>
                          {alt.formularyTier !== alt.drugListTier && (
                            <div className="text-muted-foreground">{alt.formularyTier}</div>
                          )}
                        </div>

                        <div>
                          <div className="text-sm font-medium mb-1">Flags</div>
                          <div className="flex gap-1 flex-wrap">
                            {alt.umFlags.priorAuthorization && (
                              <Badge variant="static" className="text-xs">PA</Badge>
                            )}
                            {alt.umFlags.stepTherapy && (
                              <Badge variant="static" className="text-xs">ST</Badge>
                            )}
                            {alt.umFlags.quantityLimit && (
                              <Badge variant="static" className="text-xs">QL</Badge>
                            )}
                            {!alt.umFlags.priorAuthorization && !alt.umFlags.stepTherapy && !alt.umFlags.quantityLimit && (
                              <span className="text-muted-foreground text-sm">None</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  {/* Pagination - Mobile */}
                  {totalPages > 1 && (
                    <div className="flex flex-col gap-3 mt-4">
                      <div className="text-sm text-muted-foreground text-center">
                        Page {currentPage} of {totalPages} ({searchFilteredAlternatives.length} alternatives)
                      </div>
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 rounded border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 rounded border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="therapeutic" className="mt-6">
            {therapeuticAlternativesLoading ? (
              <div className="p-12">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <p className="text-sm font-medium">Loading therapeutic alternatives...</p>
                </div>
              </div>
            ) : therapeuticAlternativesError ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <Info className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground">
                    Unable to Load Therapeutic Alternatives
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    {therapeuticAlternativesError}
                  </p>
                </div>
              </div>
            ) : !therapeuticAlternatives || therapeuticAlternatives.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <Info className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-foreground">
                    Therapeutic Alternatives Not Listed
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    No therapeutic alternatives in the same drug class are specified by this plan. Covered alternatives may still exist—check the Covered Alternatives tab.
                  </p>
                </div>
              </div>
            ) : (
              <TherapeuticAlternativesTable 
                alternatives={therapeuticAlternatives}
                originalTherapeuticClassId={originalTherapeuticClassId}
                searchQuery={searchQuery}
              />
            )}
          </TabsContent>
        </Tabs>
    </div>
  );
};

export default AlternativesTab;
